/** Query-parameter contract: ?level&variant&mirror&seed&speed&sens&spd&steer&map&maps&assist&fpv&perf&debug&player&lens&fov&q&gfx&theme&appearanceSeed. */

import { parseAppearanceSeed } from "./appearanceSeed";
import type { StickLayout } from "../input/joystick";
import { LENS_NAMES, isCameraId } from "../core/cameraFrame";
import type { CameraView, GfxTier, OnboardOptics } from "../render/scene";
import { DEFAULT_ONBOARD_OPTICS } from "../render/scene";
import { resolveSpeedProfile } from "../sim/profile";
import type { AssistMode, ControlScheme, LevelId, ScenarioConfig, Variant } from "../sim/types";
import { isAppearanceThemeId, type AppearanceThemeId } from "../render/themes";

export interface AppParams extends ScenarioConfig {
  /** Explicit ?theme choice; null means use the saved/default appearance. */
  appearanceTheme: AppearanceThemeId | null;
  /** Explicit visual replay seed; absent means fresh human exploration colors. */
  appearanceSeed: number | null;
  /** Whether ?level was given explicitly (single-level focus). */
  explicitLevel: boolean;
  debug: boolean;
  /** Scripted player name (dev/pipeline only) or null for human input. */
  player: string | null;
  /** Tuning overrides from the query (sens / spd); null = use saved/defaults. */
  tuningOverride: { sensitivity?: number; speedScale?: number } | null;
  /** Custom map to play: a name in public/maps, "draft", or a URL. */
  mapId: string | null;
  /** Which camera renders: `follow`, or one of the robot's real cameras. */
  view: CameraView;
  /** Show the internal map-editor button. `?editor=0` hides it. */
  editorButton: boolean;
  /**
   * Show the map picker. `?maps=0` hides it, for a controlled session where
   * the map is part of the condition and must not be changeable mid-study.
   */
  mapButton: boolean;
  /** `?perf=1`: the on-screen frame/judder readout, for diagnosing a device. */
  perf: boolean;
  /**
   * `?fpv=1` / `?fpv=0`: pin the playable viewpoint. Absent leaves it to the
   * player's saved choice, so a shared link can specify one without taking the
   * setting away from everyone who follows it.
   */
  playView: "third" | "first" | null;
  /**
   * `?record=name`: arm recording of the driver's view for the session, under
   * this base name. A link rather than three taps, so a data-collection
   * session can be set up once and handed to whoever is driving.
   */
  record: string | null;
  /**
   * `?case=SS-PED-01`: which catalogued situation this run stages (ADR-075).
   * Flows into ScenarioConfig.caseId and from there into the episode, so
   * demonstrations group by case. A map may also carry its own `case_id`.
   */
  caseId?: string;
  /** `?robot=bulldog|alaska|box`: which machine to draw (ADR-048). */
  robot: "bulldog" | "alaska" | "box" | null;
  /** `?env=suburban|downtown`: the world's setting (ADR-048). */
  env: "suburban" | "downtown" | null;
  /**
   * Joystick layout. `floating` (default) moves the stick to wherever the
   * thumb lands and returns it to centre on release; `fixed` is the original
   * always-centred stick, kept so the two can be compared.
   */
  stick: StickLayout;
  /** Living Safe Corridor override (ADR-065): ?corridor=0 turns it off, =1
   * forces it on. Undefined = the level default. The study's A/B lever. */
  corridor?: boolean;
  /**
   * Onboard sensor optics (ADR-064), only meaningful with `?view=<camera>`.
   *
   *   ?lens=<name>  lens profile from config/robot_cameras.json
   *   ?fov=<deg>    horizontal FOV override, for matching reference footage
   *   ?q=play|hq|max  cube face resolution: 512 / 1024 / 2048
   *
   * The onboard view defaults to `hq`, not `play`: it exists to produce data,
   * and an under-sampled cube map is a soft frame that no later step recovers.
   * `?q=play` puts it back to the phone-affordable tier.
   */
  optics: OnboardOptics;
  /**
   * `?capture=1`: offline dataset capture. Keeps the WebGL drawing buffer so a
   * frame rendered on demand can still be read back after the render loop is
   * stopped (scripts/capture_clip.mjs). Never set for a play session.
   */
  capture: boolean;
  /**
   * `?dyn=measured`: drive with the robot dynamics MEASURED from the real
   * robot's bags (config/robot_profile_measured.json, ADR-074) instead of the
   * unvalidated placeholder. Also defaults the speed profile to `current`
   * (x1.0) unless `?speed` is given, because a measured profile scaled by the
   * arcade x6 would be a different fiction. Recorded in the episode via
   * `robot_profile._status`.
   */
  dyn: "placeholder" | "measured";
  /**
   * `?splat=<url>`: load a Gaussian-splat scene (.splat/.ply/.spz) as the
   * world backdrop (ADR-069) — a neural reconstruction of the real street,
   * rendered by the same cameras that photograph the gray-box. Placement:
   * `?splatpos=x,y,z` (m), `?splatrot=x,y,z` (deg), `?splatscale=s`.
   */
  splat: string | null;
  /**
   * `?pano=<id|url>`: a panorama world — the photographs themselves on a
   * sphere around the robot, swapped station by station as it drives, with
   * nothing reconstructed (ADR-088). A bare id resolves under /panos/.
   */
  pano: string | null;
  splatPos: { x: number; y: number; z: number };
  splatRotDeg: { x: number; y: number; z: number };
  splatScale: number;
  /**
   * `?splatonly=1`: once the splat has loaded, hide the built scenery so the
   * reconstruction IS the world (ADR-086). The road network, the kerb and
   * every collision test are untouched — they read the level's geometry, not
   * its meshes — so the robot drives the same route inside real photographs.
   */
  splatOnly: boolean;
  /**
   * `?freeroam=1` (`?freecam=1` still accepted): drive the robot through a
   * reconstruction with no errand — no destination beacon, and the episode
   * never ends, so the camera never stops and no delivery report covers the
   * street. The robot itself stays, because it is what gives the scene its
   * scale and its eye height.
   */
  freeRoam: boolean;
  /**
   * `?gfx=full|lite`: pin the rendering budget. Absent means `auto`, which
   * detects a software rasterizer (SwiftShader &c.) and drops to the lite
   * tier — no MSAA, CSS resolution, cheap texture sampling. `full` is the
   * escape hatch if the detection ever fires on a machine it should not.
   */
  gfx: GfxTier;
}

const LEVELS: LevelId[] = ["cal1", "cal2", "cal3", "research", "mission"];
const VARIANTS: Variant[] = ["A", "B", "C"];

export function parseParams(search: string): AppParams {
  const q = new URLSearchParams(search);

  const levelRaw = q.get("level");
  // The one-minute delivery mission is the game's default experience; the
  // lab levels (cal1..3, research) stay addressable for controlled runs.
  const level: LevelId = LEVELS.includes(levelRaw as LevelId)
    ? (levelRaw as LevelId)
    : "mission";

  const variantRaw = (q.get("variant") ?? "A").toUpperCase();
  const variant: Variant = VARIANTS.includes(variantRaw as Variant)
    ? (variantRaw as Variant)
    : "A";

  const seedRaw = Number(q.get("seed") ?? "1");
  const seed = Number.isFinite(seedRaw) && seedRaw >= 0 ? Math.floor(seedRaw) >>> 0 : 1;

  return {
    appearanceSeed: parseAppearanceSeed(q.get("appearanceSeed")),
    appearanceTheme: (() => {
      const v = q.get("theme");
      return v !== null && isAppearanceThemeId(v) ? v : null;
    })(),
    level,
    explicitLevel: levelRaw !== null,
    variant,
    mirror: q.get("mirror") === "1",
    seed,
    speedProfile: resolveSpeedProfile(
      q.get("speed") ?? (q.get("dyn") === "measured" ? "current" : null),
      level,
    ),
    dyn: q.get("dyn") === "measured" ? "measured" : "placeholder",
    caseId: (() => {
      const v = q.get("case");
      // Catalogue ids look like SS-PED-01; anything else is not a case.
      return v && /^SS-[A-Z]{2,3}(-\d{2})?$/.test(v) ? v : undefined;
    })(),
    controlScheme: parseScheme(q.get("steer")) ?? undefined,
    stick: q.get("stick") === "fixed" ? "fixed" : "floating",
    debug: q.get("debug") === "1",
    player: q.get("player"),
    mapId: q.get("map"),
    view: (() => {
      const v = q.get("view");
      // Unknown values fall back to the participant POV rather than silently
      // rendering an engineering view.
      return v === null || v === "follow" ? "follow" : isCameraId(v) ? v : "follow";
    })(),
    editorButton: q.get("editor") !== "0",
    mapButton: q.get("maps") !== "0",
    perf: q.get("perf") === "1",
    playView: q.get("fpv") === "1" ? "first" : q.get("fpv") === "0" ? "third" : null,
    record: q.get("record"),
    robot: (() => {
      const v = q.get("robot");
      return v === "bulldog" || v === "alaska" || v === "box" ? v : null;
    })(),
    env: (() => {
      const v = q.get("env");
      return v === "suburban" || v === "downtown" ? v : null;
    })(),
    gfx: (() => {
      const v = q.get("gfx");
      return v === "full" || v === "lite" ? v : "auto";
    })(),
    // Left undefined so the simulation picks per level: real physics on the
    // mission, the assisted governor on the pinned lab levels.
    assist: parseAssist(q.get("assist")),
    corridor: q.get("corridor") === null ? undefined : q.get("corridor") !== "0",
    tuningOverride: parseTuningOverride(q),
    optics: parseOptics(q),
    capture: q.get("capture") === "1",
    splat: q.get("splat"),
    pano: ((v) => (v && !/^(https?:|\/)/.test(v) ? `panos/${v}.json` : v))(q.get("pano")),
    splatPos: parseVec3(q.get("splatpos")),
    splatRotDeg: parseVec3(q.get("splatrot")),
    splatOnly: q.get("splatonly") === "1",
    freeRoam: q.get("freeroam") === "1" || q.get("freecam") === "1",
    splatScale: (() => {
      const v = Number(q.get("splatscale"));
      return Number.isFinite(v) && v > 0 ? v : 1;
    })(),
  };
}

function parseOptics(q: URLSearchParams): OnboardOptics {
  const onboard = isCameraId(q.get("view"));
  const qRaw = q.get("q");
  const quality =
    qRaw === "play" || qRaw === "hq" || qRaw === "max"
      ? qRaw
      : onboard
        ? "hq"
        : DEFAULT_ONBOARD_OPTICS.quality;
  const fovRaw = Number(q.get("fov"));
  // A typo in ?lens must not take the whole app down (lensProfile throws on an
  // unknown name, and the Renderer is built in the Game constructor), but it
  // must not pass silently either: the lens decides the geometry of every
  // pixel. Fall back loudly. Nothing is poisoned downstream — the optics that
  // were actually used are reported by Renderer.onboardOptics and written into
  // every clip, so a capture can check it got what it asked for.
  const lensRaw = q.get("lens");
  const lens = lensRaw !== null && LENS_NAMES.includes(lensRaw) ? lensRaw : DEFAULT_ONBOARD_OPTICS.lens;
  if (lensRaw !== null && lens !== lensRaw) {
    console.error(`unknown lens profile "${lensRaw}" — using ${lens}. Known: ${LENS_NAMES.join(", ")}`);
  }
  return {
    lens,
    fovDeg: q.get("fov") !== null && Number.isFinite(fovRaw) && fovRaw > 1 && fovRaw < 359 ? fovRaw : null,
    quality,
  };
}

function parseVec3(v: string | null): { x: number; y: number; z: number } {
  const parts = (v ?? "").split(",").map(Number);
  const [x, y, z] = [parts[0], parts[1], parts[2]];
  return {
    x: Number.isFinite(x) ? (x as number) : 0,
    y: Number.isFinite(y) ? (y as number) : 0,
    z: Number.isFinite(z) ? (z as number) : 0,
  };
}

function parseAssist(v: string | null): AssistMode | undefined {
  return v === "assisted" || v === "real" ? v : undefined;
}

function parseScheme(v: string | null): ControlScheme | null {
  return v === "heading" || v === "rate" ? v : null;
}

function parseTuningOverride(
  q: URLSearchParams,
): { sensitivity?: number; speedScale?: number } | null {
  const sens = q.get("sens");
  const spd = q.get("spd");
  if (sens === null && spd === null) return null;
  const out: { sensitivity?: number; speedScale?: number } = {};
  if (sens !== null && Number.isFinite(Number(sens))) out.sensitivity = Number(sens);
  if (spd !== null && Number.isFinite(Number(spd))) out.speedScale = Number(spd);
  return out;
}

export function nextLevel(level: LevelId): LevelId | null {
  // cal1 -> cal2 -> cal3 -> research -> mission; the mission is terminal.
  const i = LEVELS.indexOf(level);
  return i >= 0 && i < LEVELS.length - 1 ? (LEVELS[i + 1] ?? null) : null;
}

export function buildSearch(p: AppParams, level: LevelId): string {
  const q = new URLSearchParams();
  q.set("level", level);
  q.set("variant", p.variant);
  q.set("mirror", p.mirror ? "1" : "0");
  q.set("seed", String(p.seed));
  if (p.speedProfile) q.set("speed", p.speedProfile);
  if (p.dyn === "measured") q.set("dyn", "measured");
  if (p.caseId) q.set("case", p.caseId);
  if (p.debug) q.set("debug", "1");
  if (p.player) q.set("player", p.player);
  if (p.assist) q.set("assist", p.assist);
  if (p.appearanceTheme) q.set("theme", p.appearanceTheme);
  if (p.appearanceSeed !== null) q.set("appearanceSeed", String(p.appearanceSeed));
  return "?" + q.toString();
}
